import { mat4 } from 'gl-matrix';
import { Context } from '../context/Context';
import { Camera } from '../camera';
import { GBuffer } from './GBuffer';
import { Flame } from '../object/primitives/Flame';

/**
 * Procedural flame pass — draws a billboard quad per `scene.flames` entry
 * with additive blend (`ONE`, `ONE`). Sits after forward transparency and
 * before TAA so flames composite over both opaque and transparent geometry.
 *
 * Flames don't write depth, so they don't sort against each other or against
 * transparent surfaces — additive blending is order-independent in colour
 * but small overlaps will read as "brighter" rather than "frontmost". For the
 * candle / campfire scale this is the desired look.
 *
 * Soft-particle fade is sampled from the gbuffer depth texture; flames poking
 * through the candle wax avoid a hard silhouette intersection.
 *
 * Pass skipped entirely when `scene.flames` is empty — zero cost on scenes
 * without authored flames.
 */
export class FlamesPass {
  private vao!: WebGLVertexArrayObject;
  // Retain JS wrappers so V8 GC doesn't drop the GL buffers from under the
  // VAO. Same precaution LiquidSurface takes.
  private buffers: WebGLBuffer[] = [];

  init(ctx: Context): void {
    const gl = ctx.gl;
    const vao = gl.createVertexArray();
    if (!vao) throw new Error('FlamesPass: failed to allocate VAO');
    this.vao = vao;
    gl.bindVertexArray(this.vao);

    // Billboard quad: x in [-QUAD_PAD, QUAD_PAD], y in [0, 1] (base→tip).
    // Anchor at base center. Padding past ±0.5 gives the flame body's wobble
    // sideways room without clipping at the silhouette — see flame-vertex /
    // flame-fragment for the body / quad coordinate split.
    const QUAD_PAD = 0.7;
    const verts = new Float32Array([
      -QUAD_PAD, 0.0,
      QUAD_PAD, 0.0,
      QUAD_PAD, 1.0,
      -QUAD_PAD, 1.0,
    ]);
    const vbo = gl.createBuffer();
    if (!vbo) throw new Error('FlamesPass: failed to allocate VBO');
    this.buffers.push(vbo);
    gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
    gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);

    const indices = new Uint16Array([0, 1, 2, 0, 2, 3]);
    const ibo = gl.createBuffer();
    if (!ibo) throw new Error('FlamesPass: failed to allocate IBO');
    this.buffers.push(ibo);
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, ibo);
    gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, indices, gl.STATIC_DRAW);

    gl.bindVertexArray(null);
  }

  render(
    ctx: Context,
    gBuffer: GBuffer,
    camera: Camera,
    flames: readonly Flame[],
    elapsedMs: number,
    outputBuffer: WebGLFramebuffer | null,
  ): void {
    if (flames.length === 0) return;
    const gl = ctx.gl;

    gl.bindFramebuffer(gl.FRAMEBUFFER, outputBuffer);
    gl.viewport(0, 0, ctx.viewPort.width, ctx.viewPort.height);
    gl.enable(gl.DEPTH_TEST);
    gl.depthMask(false);
    gl.disable(gl.CULL_FACE);
    gl.enable(gl.BLEND);
    // Additive blend — emissive flames sum onto whatever's behind them.
    gl.blendFunc(gl.ONE, gl.ONE);

    const shader = ctx.engine.shaderManager.getFlameShader();
    ctx.setShader(shader);
    gl.bindVertexArray(this.vao);

    const time = elapsedMs * 0.001;
    const camPos = camera.cameraPosition;
    // Inverse view-projection is needed by the soft-particle path to
    // reconstruct world position from sampled depth. The getter caches; one
    // read per pass is fine.
    const invVp = camera.invViewProjection;

    // VP for the vertex shader — Camera exposes modelView + projection
    // separately, compose into a module-shared scratch so we don't allocate
    // per-flame.
    const vp = scratchVp;
    mat4.multiply(vp, camera.projectionMatrix, camera.modelViewMatrix);

    for (let i = 0; i < flames.length; i++) {
      const f = flames[i];

      // Cylindrical billboard: keep world-up locked, rotate only around it
      // so the flame stays gravity-aligned. The "right" vector is the
      // horizontal-plane perpendicular to the camera→flame direction.
      const dx = f.position[0] - (camPos[0] as number);
      const dz = f.position[2] - (camPos[2] as number);
      const len = Math.hypot(dx, dz);
      // Degenerate case: camera directly above/below the flame. Pick world-X
      // as a fallback so the quad still renders (will be edge-on, but won't
      // produce NaN).
      let rx = 1;
      let rz = 0;
      if (len > 1e-5) {
        rx = dz / len;
        rz = -dx / len;
      }

      shader.uniformsContainer
        .setViewProjection(vp)
        .setFlamePosition(f.position as unknown as number[])
        .setRight([rx, 0, rz])
        .setWidth(f.width)
        .setHeight(f.height)
        .setTime(time + f.seed)
        .setIntensity(f.intensity)
        .setBaseColor(f.baseColor)
        .setMidColor(f.midColor)
        .setTipColor(f.tipColor)
        .setScrollSpeed(f.scrollSpeed)
        .setTaper(f.taper)
        .setNoiseScale(f.noiseScale)
        .setSeed(f.seed)
        .setCameraPosition(camPos as number[])
        .setInvViewProjection(invVp)
        .setSceneDepth(gBuffer.depthTexture)
        .setScreenSize([ctx.viewPort.width, ctx.viewPort.height])
        .setSoftness(f.softness);

      if (i === 0) shader.validate();
      gl.drawElements(gl.TRIANGLES, 6, gl.UNSIGNED_SHORT, 0);
    }

    gl.bindVertexArray(null);
    gl.disable(gl.BLEND);
    gl.depthMask(true);
    gl.enable(gl.CULL_FACE);
  }

  destroy(gl: WebGL2RenderingContext): void {
    if (this.vao) gl.deleteVertexArray(this.vao);
    for (const buf of this.buffers) gl.deleteBuffer(buf);
    this.buffers = [];
  }
}

// Scratch matrix reused across draws — the renderer owns one FlamesPass per
// scene, so a single shared module-scope scratch is safe.
const scratchVp = mat4.create();
